// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import translate from "@insite/client-framework/Translate";
import xPatterns, { commitMask } from "@vanmeter/XDL/Utilities/xValidate/xPatterns";
import * as Yup from "yup";

type xMasksType = {
    [key: string]: { transform: (newValue: string, oldValue: string, ...options: any) => string; validate?: string };
};

/**
 * define your mask or transformation here
 */
const xMasks: xMasksType = {
    /**
     * phone mask
     */
    phone: {
        /**
         * transform
         * @param {string} newValue
         * @param {string} oldValue
         * @param {...any} params
         * @returns manipulated string
         */
        transform: (newValue, oldValue) => commitMask(newValue, oldValue, xPatterns.phone),

        /**
         * this is a default validation for the mask, can be override if user enter additional validators
         */
        validate: "phone",
    },

    /**
     * max transformation
     */
    max: {
        transform: (newValue, oldValue, max) => String(newValue).slice(0, max),
    },

    /**
     * uppercase transformation
     */
    uppercase: {
        transform: (newValue, oldValue) => String(newValue).toUpperCase(),
    },

    /**
     * lowercase transformation
     */
    lowercase: {
        transform: (newValue, oldValue) => String(newValue).toLowerCase(),
    },

    limit: {
        transform: (newValue, oldValue, max) => String(newValue).slice(0, max),
    },
    maxValue: {
        transform: (newValue, oldValue, max) => (+newValue > max ? `${max}` : newValue),
    },
    minValue: {
        transform: (newValue, oldValue, min) => (+newValue < min ? `${min}` : newValue),
    },
};

/**
 * Define Custom Validator and Mask noEmptySpace
 * @usage noEmptySpace:force
 */
Yup.addMethod<Yup.StringSchema>(Yup.string, "noEmptySpace", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-no-empty-space`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        return (
            /^\S+$/.test(value) ||
            createError({
                path,
                message: errorMessage || "we shouldn't have any white space in this field",
            })
        );
    });
});

// define the mask for noEmptySpace:force
xMasks.noEmptySpace = {
    transform: (newValue, oldValue, options) =>
        options === "force" ? String(newValue).replace(/\s+/gm, "") : newValue,
};

/**
 * Postal Code ( ZIP Code) Validation
 */
Yup.addMethod<Yup.StringSchema>(Yup.string, "postalCode", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-no-empty-space`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        // any additional country regex can be found here
        // https://www.codegrepper.com/code-examples/whatever/validate+postal+code+in+yup
        const regex: { [key: string]: RegExp } = {
            US: /\d{5}([ \-]\d{4})?/gm,
            CA: /[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d/gm,
        };

        const country = options ?? "US";
        if (!regex[country]) {
            throw new Error(`Country ${country} is not supported validation`);
        }

        return (
            (regex[country] && regex[country].test(value)) ||
            createError({
                path,
                message: errorMessage || `${translate("Must be a valid Postal Code")}`,
            })
        );
    });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, "email", function (this: any, options: any, errorMessage: string) {
    return this.test(`test-email-address`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        return (
            // eslint-disable-next-line no-empty-character-class
            /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w{2,3}$/.test(value) ||
            createError({
                path,
                message: errorMessage || "Invalid Email Address",
            })
        );
    });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, "phone", function (this: any, _: any, errorMessage: string) {
    return this.test(`test-phone-number`, errorMessage, function (this: any, value: string) {
        const { path, createError } = this;

        return (
            /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/.test(value) ||
            createError({
                path,
                message: errorMessage || "Invalid format.  Please format as: (123) 456-7890",
            })
        );
    });
});

// postalCode Mask
xMasks.postalCode = {
    transform: (newValue, oldValue, options) => {
        const country = options ?? "US";
        if (country === "US") {
            return newValue.length > 5
                ? commitMask(newValue, oldValue, xPatterns.longZipCode)
                : commitMask(newValue, oldValue, xPatterns.shortZipCode);
        }
        return newValue;
    },
    validate: "min:5|max:10",
};

export default xMasks;
